import { FC, useState } from 'react';
import {
  Fade,
  Grid,
  Box,
  TextField,
  IconButton,
  ListItem,
  Alert,
  List,
  ListItemText,
  Autocomplete
} from '@mui/material';
import { ContentCopy, ExpandLess, ExpandMore } from '@mui/icons-material';
import { Modal, Loader, ModalSaveSection, Wysiwyg, Card } from '../../../../components';
import { deepEqual } from 'fast-equals';
import { useQuery } from 'react-query';
import { useSnackbar } from 'notistack';
import { IDropdownResponse, ISalesEmailTemplateDetail } from '../../../../models';
import {
  createSalesEmailTemplate,
  getSalesEmailTemplate,
  updateSalesEmailTemplate,
  getEmailTokens,
  getAddressBookRelationshipTypes,
  addressBookRelationshipTypesQueryKey
} from '../../../../fetch';
import { alphaSort, isEmail, stripHtmlTags } from '../../../../helpers';
import { useGetSalesUsersLookup } from '../../../../fetch/lookups';

interface IEmailTemplateModal {
  open: boolean;
  onClose: () => void;
  refetchEmailTemplates?: () => void;
  emailTemplateId?: number;
}

export const EmailTemplateModal: FC<IEmailTemplateModal> = ({
  open,
  onClose,
  refetchEmailTemplates,
  emailTemplateId
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [templateName, setTemplateName] = useState<string>('');
  const [emailBody, setEmailBody] = useState<string>('');
  const [subject, setSubject] = useState<string>('');
  const { enqueueSnackbar } = useSnackbar();
  const [initialValues, setInitialValues] = useState({
    templateName: '',
    emailBody: '',
    subject: '',
    addressBookRelationshipTypes: [],
    emails: []
  });
  const [appRolesInputValue, setInputValue] = useState('');
  const [selectedJobTitles, setSelectedJobTitles] = useState<IDropdownResponse[]>([]);
  const [isExpandedTokens, setIsExpandedTokens] = useState(false);
  const [emails, setEmails] = useState<string[]>([]);
  const [emailInputValue, setEmailInputValue] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);

  const handleAddEmail = (newValue: string[]) => {
    if (newValue.length === 0) {
      setEmailError(null);
      return setEmails([]);
    }
    const lastEmail = newValue[newValue.length - 1];
    if (isEmail(lastEmail)) {
      setEmailError(null);
      setEmails(newValue);
    } else {
      setEmailError('Invalid email address');
    }
  };

  const { isLoading: isLoadingAddressBookRelationshipTypes, data: addressBookRelationshipTypes } =
    useQuery<IDropdownResponse[], Error>([addressBookRelationshipTypesQueryKey], () =>
      getAddressBookRelationshipTypes()
    );

  const { isLoading } = useQuery<ISalesEmailTemplateDetail, Error>(
    ['getSalesEmailTemplate', emailTemplateId],
    () => getSalesEmailTemplate(emailTemplateId),
    {
      onSuccess: data => {
        setTemplateName(data.templateName);
        setSubject(data.subject);
        setEmailBody(data.body);
        const selected = addressBookRelationshipTypes.filter(type =>
          data.addressBookRelationshipTypes.includes(parseInt(type.value))
        );
        setSelectedJobTitles(selected);
        setEmails(data.emails);
        setInitialValues({
          templateName: data.templateName,
          emailBody: stripHtmlTags(data.body),
          subject: data.subject,
          addressBookRelationshipTypes: alphaSort(selected, 'description'),
          emails: data.emails
        });
      },
      enabled: !!emailTemplateId && !!addressBookRelationshipTypes
    }
  );

  const { isLoading: isLoadingEmailTokens, data: emailTokens } = useQuery<
    IDropdownResponse[],
    Error
  >(['getEmailTokens'], () => getEmailTokens());

  const handleCopyToClipboard = async (shorthand: string) => {
    try {
      await navigator.clipboard.writeText(shorthand);
      enqueueSnackbar('Copied to clipboard!', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Failed to copy!', { variant: 'error' });
    }
  };

  const { data: people, isLoading: isLoadingPeople } = useGetSalesUsersLookup();

  const handleClose = () => {
    const currentValues = {
      templateName,
      emailBody: stripHtmlTags(emailBody),
      subject,
      addressBookRelationshipTypes: selectedJobTitles,
      emails
    };
    if (!deepEqual(currentValues, initialValues)) {
      const result = window.confirm('You have unsaved changes, are you sure you want to exit?');
      if (result) {
        setTemplateName(initialValues.templateName);
        setEmailBody(initialValues.emailBody);
        setSubject(initialValues.subject);
        setSelectedJobTitles(initialValues.addressBookRelationshipTypes);
        setInputValue('');
        setEmailInputValue('');
        setEmails([]);
        setEmailError(null);
        onClose();
      } else {
        return;
      }
    } else {
      setTemplateName('');
      setSubject('');
      setEmailBody('');
      setInputValue('');
      setEmailInputValue('');
      setEmails([]);
      setEmailError(null);
      onClose();
      setInitialValues({
        templateName: '',
        emailBody: '',
        subject: '',
        addressBookRelationshipTypes: [],
        emails: []
      });
    }
  };
  return (
    <Modal
      title={emailTemplateId ? 'Edit Email Template' : 'Add New Email Template'}
      open={open}
      onClose={handleClose}
      maxWidth="md"
    >
      {(isSaving || isLoadingEmailTokens || isLoading) && (
        <Loader type="overlay" position="centered" />
      )}
      <Fade in={open}>
        <Box mt={0.5}>
          <Box mt={1} mb={1}>
            <Autocomplete
              multiple
              disabled={isLoadingAddressBookRelationshipTypes}
              loading={isLoadingAddressBookRelationshipTypes}
              id="jobTitles"
              value={alphaSort(selectedJobTitles, 'description')}
              onChange={(event, newValue: any[]) => {
                setSelectedJobTitles(newValue);
              }}
              fullWidth
              inputValue={appRolesInputValue}
              onInputChange={(event, newInputValue) => {
                setInputValue(newInputValue);
              }}
              options={addressBookRelationshipTypes ?? []}
              getOptionLabel={option => option?.description}
              filterSelectedOptions
              filterOptions={(options: IDropdownResponse[]) => {
                const filtered = options.filter(
                  option => !selectedJobTitles.some(selected => selected.value === option.value)
                );
                return filtered;
              }}
              renderOption={(props, option: IDropdownResponse) => {
                return (
                  <li {...props} key={`${option?.value}`}>
                    {option?.description}
                  </li>
                );
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  name="jobTitles"
                  size="small"
                  disabled={isLoadingAddressBookRelationshipTypes}
                  required
                  label={isLoadingAddressBookRelationshipTypes ? 'Loading...' : 'Job Title'}
                  placeholder="Add Job Title"
                />
              )}
            />
          </Box>
          <Box mt={1} mb={1}>
            <Autocomplete
              multiple
              value={emails}
              onChange={(event, newValue: string[], reason) => {
                if (reason === 'clear') {
                  setEmailError(null);
                  return setEmails([]);
                }
                if (reason === 'removeOption') {
                  const newEmails = newValue.filter(email => email !== emailInputValue);
                  return handleAddEmail(newEmails);
                }
                handleAddEmail(newValue);
              }}
              id="custom-emails"
              options={people?.map(person => person.value) ?? []}
              inputValue={emailInputValue}
              onInputChange={(event, newInputValue) => {
                setEmailInputValue(newInputValue);
              }}
              freeSolo
              fullWidth
              filterSelectedOptions
              renderInput={params => (
                <TextField
                  {...params}
                  name="custom-emails"
                  label={isLoadingPeople ? 'Loading...' : 'Emails'}
                  size="small"
                  placeholder="Add Email"
                  error={!!emailError}
                  helperText={emailError}
                />
              )}
            />
          </Box>
          {emailTokens?.length > 0 && (
            <Card
              title="Email Tokens"
              isExpanded={isExpandedTokens}
              action={
                <>
                  <IconButton
                    onClick={() => setIsExpandedTokens(!isExpandedTokens)}
                    aria-expanded={isExpandedTokens}
                    aria-label="show more"
                    sx={{ padding: 0, color: theme => theme.palette.common.white }}
                  >
                    {isExpandedTokens ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </>
              }
            >
              <Alert severity="info" sx={{ marginTop: 1, marginBottom: 0.5 }}>
                {emailTemplateId
                  ? `
                Some templates utilize tokens to inject dynamic content into sections of a template
                (i.e. @@TokenName@@). Do NOT delete these tokens as this will break certain
                functionality. Click the copy icon to insert available tokens into the template.`
                  : `
                Use tokens to dynamically insert content into your template (e.g., @@TokenName@@).
                Click the copy icon next to a token to add it to your template.
                `}
              </Alert>
              <List disablePadding sx={{ marginBottom: 0.5 }}>
                <Grid container spacing={1}>
                  {emailTokens?.map((token, index) => (
                    <Grid item xs={12} sm={6} md={4} key={`${index}`}>
                      <ListItem
                        secondaryAction={
                          <IconButton onClick={() => handleCopyToClipboard(token.shorthand)}>
                            <ContentCopy />
                          </IconButton>
                        }
                      >
                        <ListItemText primary={token.description} />
                      </ListItem>
                    </Grid>
                  ))}
                </Grid>
              </List>
            </Card>
          )}
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                disabled={isLoading || isSaving}
                variant="outlined"
                autoComplete="nope"
                label="Template Name"
                name="templateName"
                size="small"
                value={templateName}
                onChange={e => setTemplateName(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                disabled={isLoading || isSaving}
                variant="outlined"
                autoComplete="nope"
                label="Subject"
                name="subject"
                size="small"
                value={subject}
                onChange={e => setSubject(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <Wysiwyg
                readOnly={isLoading || isSaving}
                id={'message'}
                onChange={(html: string) => {
                  setEmailBody(html);
                }}
                value={emailBody}
                isRequired={true}
                label="Message"
              />
            </Grid>
          </Grid>
          <ModalSaveSection
            isSaveDisabled={
              !templateName || !subject || !emailBody || selectedJobTitles.length === 0
            }
            handleCancel={handleClose}
            handleSave={async () => {
              let successMessage = '';
              try {
                setIsSaving(true);
                if (emailTemplateId) {
                  await updateSalesEmailTemplate(emailTemplateId, {
                    templateName: templateName,
                    subject: subject,
                    body: emailBody,
                    addressBookRelationshipTypes: selectedJobTitles.map(jobTitle =>
                      parseInt(jobTitle.value)
                    ),
                    emails
                  });

                  successMessage = 'Email template updated!';
                } else {
                  await createSalesEmailTemplate({
                    templateName: templateName,
                    subject: subject,
                    body: emailBody,
                    addressBookRelationshipTypes: selectedJobTitles.map(jobTitle =>
                      parseInt(jobTitle.value)
                    ),
                    emails
                  });

                  successMessage = 'Email template created!';
                }

                enqueueSnackbar(successMessage, {
                  variant: 'success'
                });

                setTemplateName('');
                setSubject('');
                setEmailBody('');
                onClose();
                refetchEmailTemplates();
              } catch (error) {
                enqueueSnackbar(
                  error?.Detail ?? `Error creating email template, please try again.`,
                  {
                    variant: 'error'
                  }
                );
              } finally {
                setIsSaving(false);
              }
            }}
          />
        </Box>
      </Fade>
    </Modal>
  );
};
